import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Cloud, Link2, Loader2, Unlink } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/**
 * Instellingen > Koppelingen > Nextcloud (F2a, 17 oktober 2026).
 *
 * De basis onder de Nextcloud-lezers: serveradres, gebruikersnaam en een
 * app-wachtwoord. De backend toetst ze met een echte aanroep voordat er iets
 * wordt opgeslagen (`PUT /connections/nextcloud`); alleen https. Het
 * app-wachtwoord gaat na het opslaan uit de staat, zoals een sleutel in
 * `GoogleConnection`.
 *
 * Een eigen blok en niet een regel in de bronnenlijst van `GoogleConnection`:
 * die lijst toont een sleutelveld, en Nextcloud heeft er drie.
 */

export const NEXTCLOUD = "nextcloud";

/** Wie mag koppelen: eigenaar en lid, net als de backend (`mag_beslissen`). Een
 *  onbekende of ontbrekende rol telt als kijker, dus nee. PUUR. */
export function magKoppelen(rol) {
  return rol === "eigenaar" || rol === "lid";
}

export default function NextcloudConnection() {
  const { t } = useTaal();
  const [koppeling, setKoppeling] = useState(null);
  const [rol, setRol] = useState("");
  const [keuze, setKeuze] = useState({ instelling: "", geldt: "google" });
  const [laden, setLaden] = useState(true);
  const [server, setServer] = useState("");
  const [gebruiker, setGebruiker] = useState("");
  const [wachtwoord, setWachtwoord] = useState("");
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");

  const lees = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/connections`);
      const lijst = Array.isArray(r.data?.koppelingen) ? r.data.koppelingen : [];
      setKoppeling(lijst.find(k => k.bron === NEXTCLOUD && k.actief) || null);
    } catch {
      setKoppeling(null);
    }
    // De rol apart: lukt die niet, dan geen formulier (de backend weigert dan
    // toch met 403), wel de rest van het blok.
    try {
      const k = await axios.get(`${API}/connections/bronkeuze`);
      if (k.data?.geldt) setKeuze(k.data);
    } catch {
      // Zonder keuze toont het blok de schakelaar niet; er verandert niets.
    }
    try {
      const o = await axios.get(`${API}/organisatie`);
      setRol(o.data?.mijn_rol || "");
      // F3a (Frank, 20 oktober 2026): het data-thuis-adres van de organisatie vult het
      // serveradres alleen VOOR, en nooit over wat er al staat. Inloggen doet ieder zelf.
      const thuis = o.data?.data_thuis;
      if (typeof thuis === "string" && thuis) setServer((s) => s || thuis);
    } catch {
      setRol("");
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => { lees(); }, [lees]);

  const koppel = async () => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.put(`${API}/connections/nextcloud`, { server, gebruiker, wachtwoord });
      setKoppeling(r.data);
      setWachtwoord("");
      toast.success(t("{bron} gekoppeld", { bron: "Nextcloud" }));
    } catch (e) {
      const reden = e.response?.data?.detail || t("Koppelen is niet gelukt. Er is niets opgeslagen.");
      setFout(reden);
      toast.error(reden);
    } finally {
      setBezig(false);
    }
  };

  const kies = async (waarde) => {
    try {
      const r = await axios.put(`${API}/connections/bronkeuze`, { keuze: waarde });
      setKeuze(r.data);
      toast.success(t("Bronkeuze bewaard"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Dat lukte niet. Er is niets gewijzigd."));
    }
  };

  const ontkoppel = async () => {
    setBezig(true);
    try {
      await axios.delete(`${API}/connections/${koppeling.id}`);
      setKoppeling(null);
      toast.success(t("Nextcloud is hier ontkoppeld"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Ontkoppelen is niet gelukt"));
    } finally {
      setBezig(false);
    }
  };

  if (laden) {
    return <Loader2 className="h-4 w-4 animate-spin" aria-label={t("Laden")} />;
  }

  const volledig = server.trim() && gebruiker.trim() && wachtwoord.trim();

  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid="bron-nextcloud">
      <div className="flex items-start gap-3">
        <Cloud className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))] shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Nextcloud</p>
          <p className="text-xs text-muted-foreground">
            {t("Je eigen Nextcloud-server, met een app-wachtwoord. Bestanden, agenda, contacten en meldingen volgen per onderdeel.")}
          </p>
        </div>
      </div>

      {fout && (
        <p className="text-xs text-[hsl(var(--accent))]" data-testid="fout-nextcloud">
          <AlertTriangle className="h-3 w-3 inline mr-1" />
          {fout}
        </p>
      )}

      {koppeling ? (
        <div className="space-y-2" data-testid="actief-nextcloud">
          <p className="text-xs">
            {t("Gekoppeld")} - {koppeling.account}
            {koppeling.server ? ` (${koppeling.server.replace(/^https:\/\//, "")})` : ""}
            {koppeling.gekoppeld_op ? ` ${t("sinds {wanneer}", { wanneer: koppeling.gekoppeld_op.slice(0, 10) })}` : ""}.
          </p>
          <Button variant="outline" size="sm" disabled={bezig} onClick={ontkoppel}
                  data-testid="ontkoppel-nextcloud">
            {bezig ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Unlink className="h-3 w-3 mr-1" />}
            {t("Ontkoppelen")}
          </Button>
          <p className="text-xs text-muted-foreground">
            {t("Ontkoppelen vergeet het app-wachtwoord hier. Het blijft geldig in Nextcloud tot je het daar intrekt.")}
          </p>
          {/* F2j: per lid EEN bron voor agenda (ochtendronde) en bestandsvragen. */}
          <div className="space-y-1 pt-1" data-testid="bronkeuze">
            <label htmlFor="bronkeuze-keuze" className="text-xs font-medium">
              {t("Agenda en bestanden uit")}
            </label>
            <select id="bronkeuze-keuze" value={keuze.geldt}
                    onChange={e => kies(e.target.value)}
                    className="h-9 rounded-md border bg-background px-2 text-sm"
                    data-testid="bronkeuze-keuze">
              <option value="google">Google</option>
              <option value="nextcloud">Nextcloud</option>
            </select>
            <p className="text-xs text-muted-foreground">
              {t("Geldt voor de agenda in je ochtendronde en voor vragen over je bestanden. De Nextcloud-agenda komt met een volgende versie.")}
            </p>
          </div>
        </div>
      ) : !magKoppelen(rol) ? (
        <p className="text-xs text-muted-foreground" data-testid="kijker-nextcloud">
          {t("Je bent kijker in deze organisatie. Alleen een eigenaar of lid kan Nextcloud koppelen.")}
        </p>
      ) : (
        <div className="space-y-2">
          <Input value={server} onChange={e => setServer(e.target.value)}
                 placeholder="https://cloud.example.com" aria-label={t("Serveradres")}
                 data-testid="nextcloud-server" />
          <Input value={gebruiker} onChange={e => setGebruiker(e.target.value)}
                 aria-label={t("Gebruikersnaam")} placeholder={t("Gebruikersnaam")}
                 autoComplete="off" data-testid="nextcloud-gebruiker" />
          <Input type="password" value={wachtwoord} onChange={e => setWachtwoord(e.target.value)}
                 aria-label={t("App-wachtwoord")} placeholder={t("App-wachtwoord")}
                 autoComplete="new-password" data-testid="nextcloud-wachtwoord" />
          <p className="text-xs text-muted-foreground">
            {t("Maak het app-wachtwoord in Nextcloud bij Instellingen > Beveiliging. Alleen https.")}
          </p>
          <Button size="sm" disabled={bezig || !volledig} onClick={koppel}
                  data-testid="koppel-nextcloud">
            {bezig ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Link2 className="h-3 w-3 mr-1" />}
            {t("Koppelen")}
          </Button>
        </div>
      )}
    </div>
  );
}
